/**
 * useNoteAuditsList Hook
 *
 * Fetches and manages note audits list with pagination
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminNotesQuery } from '../store/adminApi';
import { useGetDashboardNotesQuery } from '../store/api';
import type { NoteAuditListItem } from '../types';

interface UseNoteAuditsListOptions {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Filter by specific user ID (legacy single user) */
  userId?: string;
  /** Comma-separated user IDs to filter by */
  userIds?: string;
  /** Filter by completion status */
  isCompleted?: boolean;
  /** Current page number (1-indexed) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Period in days */
  days?: number;
  /** Explicit start date (ISO 8601 format) */
  dateFrom?: string;
  /** Explicit end date (ISO 8601 format) */
  dateTo?: string;
}

interface UseNoteAuditsListResult {
  /** List of note audits */
  audits: NoteAuditListItem[];
  /** Total number of audits */
  total: number;
  /** Current page */
  page: number;
  /** Items per page */
  perPage: number;
  /** Whether there are more items */
  hasMore: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether data is being fetched (including refetches) */
  isFetching: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useNoteAuditsList = (
  options: UseNoteAuditsListOptions = {},
): UseNoteAuditsListResult => {
  // Build query params from options using generated types
  const queryParams: {
    isCompleted?: boolean | null;
    page?: number;
    perPage?: number;
    days?: number | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    userIds?: string | null;
  } = {};

  if (options.isCompleted !== undefined) {
    queryParams.isCompleted = options.isCompleted;
  }
  if (options.page !== undefined) {
    queryParams.page = options.page;
  }
  if (options.perPage !== undefined) {
    queryParams.perPage = options.perPage;
  }
  if (options.days !== undefined) {
    queryParams.days = options.days;
  }
  if (options.dateFrom) {
    queryParams.dateFrom = options.dateFrom;
  }
  if (options.dateTo) {
    queryParams.dateTo = options.dateTo;
  }
  // Use userIds if provided, otherwise fall back to single userId
  if (options.userIds) {
    queryParams.userIds = options.userIds;
  } else if (options.userId) {
    queryParams.userIds = options.userId;
  }

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminNotesQuery(
    options.organizationId
      ? {
          organizationId: options.organizationId,
          ...queryParams,
        }
      : skipToken,
  );

  const providerQuery = useGetDashboardNotesQuery(
    !options.organizationId
      ? (Object.keys(queryParams).length > 0 ? queryParams : ({} as any))
      : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, isFetching, error, refetch } = options.organizationId
    ? adminQuery
    : providerQuery;

  const audits = useMemo(() => data?.audits || [], [data]);
  const total = data?.total || 0;
  const page = data?.page || 1;
  const perPage = data?.per_page || 20;
  const hasMore = data?.has_more || false;
  const isEmpty = !isLoading && audits.length === 0;

  return {
    audits,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    isFetching,
    error: error ? 'Failed to load note audits' : null,
    refetch,
    isEmpty,
  };
};

export default useNoteAuditsList;
